import { useRef, useSyncExternalStore } from 'react';
import { IS_SERVER } from '../ssr';

function isShallowEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (object1[key] !== object2[key]) {
      return false;
    }
  }

  return true;
}

const getConnection = () => {
  return IS_SERVER
    ? null
    : navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
};

const useNetworkStateSubscribe = (callback) => {
  window.addEventListener('online', callback, { passive: true });
  window.addEventListener('offline', callback, { passive: true });

  const connection = getConnection();

  if (connection) {
    connection.addEventListener('change', callback, { passive: true });
  }

  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);

    if (connection) {
      connection.removeEventListener('change', callback);
    }
  };
};

const serverSnapshotCache = {
  online: true,
};
const getNetworkStateServerSnapshot = () => serverSnapshotCache;

interface NetworkState {
  online: boolean;
  downlink?: string;
  downlinkMax?: string;
  effectiveType?: string;
  rtt?: string;
  saveData?: string;
  type?: string;
}

export function useNetworkState() {
  const cache = useRef({ online: true } as NetworkState);

  const getSnapshot = () => {
    const online = IS_SERVER ? true : navigator.onLine;
    const connection = getConnection();

    const nextState: NetworkState = {
      online,
      downlink: connection?.downlink,
      downlinkMax: connection?.downlinkMax,
      effectiveType: connection?.effectiveType,
      rtt: connection?.rtt,
      saveData: connection?.saveData,
      type: connection?.type,
    };

    if (isShallowEqual(cache.current, nextState)) {
      return cache.current;
    } else {
      cache.current = nextState;
      return nextState;
    }
  };

  return useSyncExternalStore(useNetworkStateSubscribe, getSnapshot, getNetworkStateServerSnapshot);
}
